import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import { LockKeyhole, RefreshCw } from "lucide-react";
import { gmail } from "../../lib/api";
import { Button, toast, useConfirm } from "@/ui";

const CONNECTION_QUERY_KEY = ["gmail", "connection"] as const;

export default function GmailSettings() {
    const queryClient = useQueryClient();
    const confirm = useConfirm();
    const connectionQuery = useQuery({ queryKey: CONNECTION_QUERY_KEY, queryFn: gmail.connection });
    const connection = connectionQuery.data?.connection;
    const connect = useMutation({
        mutationFn: gmail.startConnection,
        onSuccess: result => { window.location.href = result.authorizationUrl; },
        onError: error => toast.error(String((error as any).body?.detail ?? (error as Error).message)),
    });
    const sync = useMutation({
        mutationFn: gmail.sync,
        onSuccess: async result => {
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: CONNECTION_QUERY_KEY }),
                queryClient.invalidateQueries({ queryKey: ["gmail", "threads"] }),
            ]);
            toast.success(result.synced ? `Imported ${result.synced} Gmail message${result.synced === 1 ? "" : "s"}.` : "Gmail is up to date.");
        },
        onError: error => toast.error(String((error as any).body?.detail ?? (error as Error).message)),
    });
    const disconnect = useMutation({
        mutationFn: gmail.disconnect,
        onSuccess: async () => {
            queryClient.removeQueries({ queryKey: ["gmail"] });
            await queryClient.invalidateQueries({ queryKey: CONNECTION_QUERY_KEY });
            toast.success("Gmail disconnected and private cached messages removed.");
        },
        onError: error => toast.error(String((error as any).body?.detail ?? (error as Error).message)),
    });

    const remove = async () => {
        const approved = await confirm({
            title: "Disconnect private Gmail?",
            description: "This permanently removes the encrypted Google credentials and private cached messages from Kardoe. Messages you explicitly shared to the CRM remain there.",
            destructive: true,
            confirmLabel: "Disconnect and remove",
        });
        if (approved) disconnect.mutate();
    };

    return (
        <section className="settings-integration-section">
            <div className="gmail-settings-heading"><LockKeyhole /><div><h3>Private Gmail</h3><p className="muted">Your kardoe.com mailbox is owner-only. Messages become visible to the team only when you share them.</p></div></div>
            {connectionQuery.isLoading ? <p className="muted">Checking Gmail connection…</p> : connection ? (
                <div className="gmail-settings-status">
                    <div>
                        <span className="chip">Connected · {connection.providerEmail}</span>
                        <small className="muted">
                            {connection.lastSyncedAt ? `Last synced ${new Date(connection.lastSyncedAt).toLocaleString()}` : "Waiting for first sync"}
                            {connection.backfillCompletedAt ? ` · ${connection.threadCount} threads` : " · importing the last 90 days"}
                        </small>
                        {connection.lastError && <small className="error">{connection.lastError}</small>}
                    </div>
                    <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                        <Link className="button-link" to="/mail/personal">Open My Gmail</Link>
                        <Button size="sm" onClick={() => sync.mutate()} disabled={sync.isPending}><RefreshCw className={sync.isPending ? "is-spinning" : ""} /> {sync.isPending ? "Syncing…" : "Sync now"}</Button>
                        <Button size="sm" variant="danger" onClick={() => void remove()} disabled={disconnect.isPending}>{disconnect.isPending ? "Removing…" : "Disconnect"}</Button>
                    </div>
                </div>
            ) : (
                <div>
                    {!connectionQuery.data?.configured && <p className="error">Google OAuth credentials have not been configured on this deployment.</p>}
                    <Button onClick={() => connect.mutate()} disabled={connect.isPending || !connectionQuery.data?.configured}>
                        {connect.isPending ? "Opening Google…" : "Connect kardoe.com Gmail"}
                    </Button>
                </div>
            )}
        </section>
    );
}
